import { ThemeToggle } from "@/components/ui/theme";
import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import Sidebar from "./Sidebar";
import { Menu, X, LogOut, Globe, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AssistantProvider } from "@/features/assistant/contexts/AssistantContext";
import { AssistantWidget } from "@/features/assistant/components/AssistantWidget";
import { getRouteParent } from "@/lib/route-parents";

export const DashboardLayout: React.FC = () => {
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const { user, logout } = useAuth();
	const { language, setLanguage, isRTL } = useLanguage();
	const navigate = useNavigate();
	const { toast } = useToast();
	const { t } = useTranslation();
	const { pathname, search } = useLocation();
	// Back goes up one level in the app (detail → list), not to the previous browser page.
	const parent = getRouteParent(pathname, search);

	// Off-screen navigation is inert. The open mobile drawer traps focus and restores it on close.
	useEffect(() => {
		const sidebar = document.getElementById("workspace-navigation");
		const content = document.getElementById("workspace-content");
		const media = window.matchMedia("(min-width: 1024px)");
		const previous = document.activeElement as HTMLElement | null;
		const sync = () => {
			sidebar?.toggleAttribute("inert", !media.matches && !sidebarOpen);
			content?.toggleAttribute("inert", !media.matches && sidebarOpen);
		};
		sync();
		media.addEventListener("change", sync);
		const focusable = () =>
			Array.from(
				sidebar?.querySelectorAll<HTMLElement>("a[href],button:not([disabled]),input:not([disabled])") || [],
			).filter(el => el.getBoundingClientRect().height > 0);
		if (sidebarOpen && !media.matches) focusable()[0]?.focus();
		const onKeyDown = (event: KeyboardEvent) => {
			if (!sidebarOpen || media.matches) return;
			if (event.key === "Escape") setSidebarOpen(false);
			if (event.key === "Tab") {
				const nodes = focusable();
				const first = nodes[0];
				const last = nodes[nodes.length - 1];
				if (event.shiftKey && document.activeElement === first) {
					event.preventDefault();
					last?.focus();
				} else if (!event.shiftKey && document.activeElement === last) {
					event.preventDefault();
					first?.focus();
				}
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => {
			media.removeEventListener("change", sync);
			window.removeEventListener("keydown", onKeyDown);
			content?.removeAttribute("inert");
			if (sidebarOpen && !media.matches) previous?.focus();
		};
	}, [sidebarOpen]);

	// Helper to get photo URL
	const getPhotoUrl = (photo: string | undefined | null): string | null => {
		if (!photo) return null;
		if (photo.startsWith("http://") || photo.startsWith("https://") || photo.startsWith("data:")) {
			return photo;
		}
		// If it's a relative path, construct full URL
		const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace("/api", "");
		return `${baseUrl}${photo.startsWith("/") ? photo : `/${photo}`}`;
	};

	const handleLogout = () => {
		logout();
		toast({
			title: t("common.success"),
			description: t("auth.loggedOut"),
		});
		navigate("/login");
	};

	const toggleLanguage = () => {
		setLanguage(language === "en" ? "ar" : "en");
	};

	const base =
		"fixed inset-y-0 z-40 min-w-60 w-60 shrink-0 bg-card border-end border-border transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 print:hidden";

	return (
		// AssistantProvider wraps the routed Outlet so the conversation survives page navigation.
		<AssistantProvider>
			<div className="admin-shell flex w-full min-h-screen min-w-0 overflow-x-hidden bg-background print:block print:min-h-0">
				<a
					href="#main-content"
					className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:start-2 focus:z-50 bg-card p-3 rounded-lg"
				>
					{t("workspace.skip")}
				</a>
				{/* Mobile sidebar overlay */}
				{sidebarOpen && (
					<div
						className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
						onClick={() => setSidebarOpen(false)}
					/>
				)}

				{/* Sidebar */}
				<div
					id="workspace-navigation"
					className={`admin-sidebar ${base} ${isRTL ? "right-0" : "left-0"} ${sidebarOpen ? "" : isRTL ? "translate-x-full" : "-translate-x-full"}`}
				>
					<div className="flex flex-col h-full">
						{/* Logo */}
						<div className="flex items-center justify-between h-16 min-h-16 px-4 border-b border-border">
							<div className="flex items-center gap-3">
								<img src="/images/logo.png" alt="" className="w-8 h-8" />
								<span className="font-semibold text-sm">{t("workspace.title")}</span>
							</div>
							<Button
								variant="secondary"
								size="sm"
								className="lg:hidden"
								onClick={() => setSidebarOpen(false)}
								aria-label={t("common.close")}
							>
								<X className="w-5 h-5" />
							</Button>
						</div>

						{/* Navigation */}
						<Sidebar isCollapsed={false} onNavigate={() => setSidebarOpen(false)} />
					</div>
				</div>

				{/* Main content - flex-1 takes remaining space, min-w-0 allows shrinking to prevent overflow */}
				<div id="workspace-content" className="flex h-dvh flex-1 min-w-0 flex-col print:block print:h-auto">
					{/* Top navigation */}
					<header className="bg-card border-b border-border min-h-16 shrink-0 flex flex-wrap items-center justify-between gap-2 px-3 py-2 sm:px-6 print:hidden">
						<div className="flex items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								className="lg:hidden"
								onClick={() => setSidebarOpen(true)}
								aria-label={t("common.menu")}
								aria-expanded={sidebarOpen}
								aria-controls="workspace-navigation"
							>
								<Menu className="w-7 h-7" />
							</Button>

							{parent && (
								<Button
									variant="secondary"
									size="lg"
									className="px-3 md:px-4"
									onClick={() => (parent.to ? navigate(parent.to) : navigate(-1))}
									aria-label={t(parent.labelKey)}
									title={t(parent.labelKey)}
								>
									<ArrowLeft className="h-4 w-4 rtl:rotate-180 md:me-2" />
									<span className="hidden md:inline">{t(parent.labelKey)}</span>
								</Button>
							)}
						</div>

						<div className="flex items-center gap-2">
							<ThemeToggle />
							<AssistantWidget />
							<button
								type="button"
								className="h-11 px-2 sm:px-4 rounded-[var(--radius-button)] hover:bg-accent transition-colors"
								onClick={() => user && navigate("/profile")}
								aria-label={t("navigation.profile")}
								title={t("navigation.profile")}
							>
								<div className="flex items-center gap-2">
									{getPhotoUrl(user?.photo || user?.avatar) ? (
										<div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center overflow-hidden">
											<img
												src={getPhotoUrl(user?.photo || user?.avatar) || ""}
												alt={
													user?.fullName ||
													[user?.firstName, user?.lastName].filter(Boolean).join(" ")
												}
												className="w-8 h-8 rounded-full object-cover"
											/>
										</div>
									) : (
										<div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
											<span className="text-md font-medium text-primary">{"👤"}</span>
										</div>
									)}
									<div className="hidden sm:block flex-1 text-start">
										<p className="text-sm font-medium text-foreground truncate leading-tight">
											{user
												? user.fullName ||
													[user.firstName, user.lastName].filter(Boolean).join(" ")
												: ""}
										</p>
									</div>
								</div>
							</button>
							<div className="divider hidden sm:block h-8 w-px bg-muted"></div>
							{/* Language toggle: labelled with the language it switches to */}
							<Button
								variant="ghost"
								size="sm"
								onClick={toggleLanguage}
								className="h-11 min-w-12 px-3 rounded-md gap-2"
								aria-label={language === "en" ? "العربية" : "English"}
								title={language === "en" ? "العربية" : "English"}
							>
								<Globe className="w-5 h-5 text-primary" />
								<span className="hidden md:inline text-sm">
									{language === "en" ? "العربية" : "English"}
								</span>
							</Button>
							<div className="divider hidden sm:block h-8 w-px bg-muted"></div>
							{/* Logout */}
							<Button
								variant="ghost"
								size="sm"
								onClick={handleLogout}
								className="h-11 w-12 rounded-md"
								aria-label={t("auth.logout")}
								title={t("auth.logout")}
							>
								<LogOut className="w-5 h-5 text-destructive" />
							</Button>
						</div>
					</header>

					{/* Page content */}
					{/* Printed pages flow at full height (no inner scroll box, no gray page background). */}
					<main
						id="main-content"
						tabIndex={-1}
						className="admin-main bg-background flex-1 min-h-0 overflow-y-auto print:h-auto print:max-h-none print:overflow-visible print:bg-card print:p-0"
					>
						<Outlet />
					</main>
				</div>
			</div>
		</AssistantProvider>
	);
};
